<template>
    <button
        :type="type"
        :id="id"
        :class="computedClass"
        :disabled="loading || disabled"
        @click="handleClick"
        @focus="isFocused = true"
        @blur="isFocused = false"
        :style="{
            height: customHeight <= 40 ? customHeight + 'px' : normalizeSizeVertical(customHeight, true),
            minHeight: '26px',
            lineHeight: 1,
            fontSize: normalizeFontSize(customFontSize, true),
            borderRadius: normalizeSizeVertical(customHeight / 3.3),
        }"
    >
        <span v-if="loading" :class="{ 'ppv2-mr-2': $slots.default }">
            <svg
                class="ppv2-animate-spin ppv2-h-4 ppv2-w-4 ppv2-text-current"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
            >
                <circle class="ppv2-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path
                    class="ppv2-opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
            </svg>
        </span>
        <slot />
    </button>
</template>

<script>
import normalizeSize from '../../utils/normalizeSize'
import normalizeSizeVertical from '../../utils/normalizeSizeVertical'
import normalizeFontSize from '../../utils/normalizeFontSize'

export default {
    name: 'BaseButton',
    data() {
        return {
            isFocused: false,
        }
    },
    props: {
        type: {
            type: String,
            default: 'button',
        },
        id: {
            type: String,
            default: null,
        },
        variant: {
            type: String,
            default: 'dark',
        },
        customHeight: {
            type: Number,
            default: 54,
        },
        customFontSize: {
            type: Number,
            default: 16,
        },
        customClass: {
            type: String,
            default: '',
        },
        loading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        computedClass() {
            const base =
                /*ppv2-min-w-[195px]*/
                ' ppv2-flex ppv2-border ppv2-text-center ppv2-transition-all ppv2-duration-300 ppv2-cursor-pointer ppv2-outline-none ppv2-border-solid ppv2-items-center ppv2-justify-center disabled:ppv2-cursor-not-allowed disabled:ppv2-opacity-60 '
            const variants = {
                dark: 'ppv2-px-6 ppv2-py-2 ppv2-bg-gray-900 text-white hover:ppv2-bg-gray-800',
                outline:
                    'ppv2-px-6 ppv2-py-2 ppv2-bg-transparent ppv2-border-[1.83px] ppv2-text-black ppv2-border-black ppv2-border-opacity-50 focus:ppv2-border-opacity-100',
                dangerOutline:
                    'ppv2-px-6 ppv2-py-2 ppv2-bg-transparent ppv2-border-[1.83px] !ppv2-text-[#911414] ppv2-border-[#911414] ppv2-border-opacity-50 focus:ppv2-border-opacity-100',
                secondary: `ppv2-inline-flex ppv2-border-none ppv2-align-center ppv2-gap-[10px] dark-bg-white-opacity ppv2-bg-black ppv2-bg-opacity-10 hover:bg-opacity-20 ppv2-text-black ppv2-px-3 ppv2-py-1 ppv2-shadow-none !ppv2-h-auto`,
                primary:
                    'ppv2-px-6 ppv2-py-2 ppv2-border-[1.83px] hover:ppv2-shadow-[inset_0_0_0_1000px_#00000066] ppv2-bg-gradient-to-r ppv2-from-[#40216F] ppv2-to-[#182471] !ppv2-text-white ppv2-border-white/50 focus:border-purple',
                danger: 'ppv2-px-6 ppv2-py-3 ppv2-bg-[#911414] text-white hover:ppv2-bg-opacity-80',
                purple: 'ppv2-px-6 ppv2-py-2 ppv2-bg-[#7D2DBD] hover:ppv2-bg-[#6b26a3] !ppv2-text-white ppv2-border-transparent ppv2-shadow-lg',
                navyOutline:
                    'ppv2-px-6 ppv2-py-2 ppv2-bg-transparent ppv2-border-[1.5px] ppv2-border-[#021832] !ppv2-text-[#021832] hover:ppv2-bg-[#f3eafa] hover:ppv2-border-[#7D2DBD] hover:!ppv2-text-[#7D2DBD] hover:ppv2-shadow-[0_4px_14px_rgba(125,45,189,0.18)] dark:ppv2-border-white/60 dark:!ppv2-text-white',
            }
            return `${base} ${variants[this.variant] || variants.dark} ${this.customClass}`.trim()
        },
        isDark() {
            return this.theme.mode === 'dark'
        },
    },
    watch: {},
    methods: {
        normalizeFontSize,
        normalizeSizeVertical,
        normalizeSize,
        handleClick(event) {
            if (this.loading || this.disabled) return
            this.$emit('click', event)
        },
    },
}
</script>

<style scoped lang="scss">
.ppv2-animate-spin {
    animation: spin 1s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>
